import React from 'react';
import { Button, Stack } from '@mui/material';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useTranslation } from 'react-i18next';

import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { theme } from '../../../theme';

const buttonSx = {
  width: '100%',
  py: 1.5,
  borderRadius: '10px',
  fontWeight: 700,
  textTransform: 'none',
  transition: 'all 0.25s ease',
};

const SalonPrimaryActions = ({
  centered = false,
  inline = false,
  lightSurface = false,
  clientChatEnabled,
  onBook,
  onContact,
  bookLabel,
}) => {
  const { t } = useTranslation();
  const { tokens: salonTheme } = useSalonPublicTheme();

  return (
    <Stack
      data-testid="salon-primary-actions"
      data-layout={inline ? 'horizontal' : 'vertical'}
      data-surface={lightSurface ? 'light' : 'overlay'}
      direction={inline ? 'row' : 'column'}
      spacing={1.25}
      useFlexGap
      style={centered ? { marginInline: 'auto' } : undefined}
      sx={{
        width: inline ? { xs: '100%', md: 480 } : { xs: '100%', md: 250 },
        maxWidth: '100%',
        flexWrap: inline ? { xs: 'wrap', sm: 'nowrap' } : 'nowrap',
        flexShrink: 0,
        '& > .MuiButton-root': inline ? {
          flex: '1 1 180px',
          minWidth: 0,
        } : undefined,
      }}
    >
      <Button
        variant="contained"
        color="inherit"
        startIcon={<CalendarMonthOutlinedIcon />}
        onClick={onBook}
        sx={{
          ...buttonSx,
          py: lightSurface ? 1.1 : buttonSx.py,
          backgroundColor: lightSurface ? salonTheme.salon.primary : 'rgba(255, 255, 255, 0.9)',
          color: lightSurface ? salonTheme.salon.onPrimary : theme.colors.primary[600],
          border: lightSurface
            ? `2px solid ${salonTheme.salon.primary}`
            : '2px solid rgba(255, 255, 255, 0.5)',
          boxShadow: lightSurface
            ? `0 8px 20px ${salonTheme.salon.primary}2E`
            : '0 4px 12px rgba(255, 255, 255, 0.3)',
          '&:hover': {
            backgroundColor: lightSurface ? salonTheme.colors.primary[700] : 'white',
            color: lightSurface ? salonTheme.salon.onPrimary : theme.colors.primary[700],
            borderColor: lightSurface ? salonTheme.colors.primary[700] : 'white',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 22px rgba(255, 255, 255, 0.4)',
          },
        }}
      >
        {bookLabel || t('actions.book')}
      </Button>

      {clientChatEnabled === true && (
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<ChatBubbleOutlineIcon />}
          onClick={onContact}
          sx={{
            ...buttonSx,
            py: lightSurface ? 1.1 : buttonSx.py,
            color: lightSurface ? salonTheme.salon.secondary : theme.colors.grey[900],
            borderColor: lightSurface
              ? salonTheme.salon.border
              : 'rgba(255, 255, 255, 0.78)',
            backgroundColor: lightSurface
              ? salonTheme.colors.background.primary
              : 'rgba(250, 239, 230, 0.24)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              borderColor: lightSurface ? salonTheme.salon.primary : 'white',
              backgroundColor: lightSurface
                ? `${salonTheme.salon.primary}14`
                : 'rgba(250, 239, 230, 0.46)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {t('client_messaging.write_to_salon', 'Écrire au salon')}
        </Button>
      )}
    </Stack>
  );
};

export default SalonPrimaryActions;
